import { useState, useMemo, useCallback } from 'react';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import { FilterOutputProps, FilterMode } from './types';
import { parseJsonToFields, computeSchemaHash, getAllPaths, getChildPaths } from './outputFilterUtils';
import FilterFieldList from './FilterFieldList';
import FilterPreview from './FilterPreview';

const DARK_COLORS = {
  background: '#1D2939',
  headerBg: '#101828',
  text: '#D0D5DD',
  textSecondary: '#667085',
  border: '#344054',
  accent: '#2ED3B7',
  accentHover: '#26b8a0',
  tabActive: '#2ED3B7',
  tabInactive: '#667085',
  tabBg: '#344054',
  buttonSecondary: '#344054',
  buttonSecondaryText: '#D0D5DD',
};

const LIGHT_COLORS = {
  background: '#FFFFFF',
  headerBg: '#F9FAFB',
  text: '#101828',
  textSecondary: '#667085',
  border: '#EAECF0',
  accent: '#0D9488',
  accentHover: '#0b7e74',
  tabActive: '#0D9488',
  tabInactive: '#667085',
  tabBg: '#F2F4F7',
  buttonSecondary: '#F2F4F7',
  buttonSecondaryText: '#344054',
};

function isValidJson(str: string): boolean {
  if (!str || !str.trim()) return false;
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
}

export default function FilterOutput({ jsonOutput, filterConfig, onApply, onClear, onClose, theme }: FilterOutputProps) {
  const isDark = theme === 'dark';
  const colors = isDark ? DARK_COLORS : LIGHT_COLORS;
  const hasValidJson = isValidJson(jsonOutput);
  const [selectedPaths, setSelectedPaths] = useState<string[]>(filterConfig?.selectedPaths ?? []);
  const [mode, setMode] = useState<FilterMode>(filterConfig?.mode ?? 'structured');
  const fields = useMemo(() => {
    if (!hasValidJson) return [];
    return parseJsonToFields(jsonOutput);
  }, [jsonOutput, hasValidJson]);
  const allLeafPaths = useMemo(() => getAllPaths(fields), [fields]);
  const handleTogglePath = useCallback((path: string) => {
    const field = findFieldByPath(fields, path);
    if (!field) return;
    const childLeafPaths = getChildPaths(field);
    setSelectedPaths((prev) => {
      const prevSet = new Set(prev);
      const allChildrenSelected = childLeafPaths.every((p) => prevSet.has(p));
      if (allChildrenSelected) {
        return prev.filter((p) => !childLeafPaths.includes(p));
      }
      const next = new Set(prev);
      for (const p of childLeafPaths) {
        next.add(p);
      }
      return [...next];
    });
  }, [fields]);
  const handleToggleAll = useCallback(() => {
    setSelectedPaths((prev) => {
      if (prev.length === allLeafPaths.length) return [];
      return [...allLeafPaths];
    });
  }, [allLeafPaths]);
  const handleDone = useCallback(() => {
    const schemaHash = computeSchemaHash(jsonOutput);
    onApply({ selectedPaths, mode, schemaHash });
    onClose();
  }, [selectedPaths, mode, jsonOutput, onApply, onClose]);
  const handleClear = useCallback(() => {
    onClear();
    onClose();
  }, [onClear, onClose]);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: colors.background,
        borderRadius: '8px',
        border: `1px solid ${colors.border}`,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ padding: '16px 16px 12px', borderBottom: `1px solid ${colors.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: colors.text }}>
            Filter Output
          </Typography>
          {hasValidJson && (
            <Chip
              label="Data"
              size="small"
              sx={{
                height: '20px',
                fontSize: '11px',
                fontWeight: 600,
                backgroundColor: isDark ? '#053321' : '#ECFDF3',
                color: isDark ? '#75E0A7' : '#027A48',
              }}
            />
          )}
        </Box>
        <Typography sx={{ fontSize: '13px', color: colors.textSecondary }}>
          Choose what passes to the next step
        </Typography>
      </Box>
      {!hasValidJson ? (
        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', padding: '32px 16px' }}>
          <Typography sx={{ fontSize: '13px', color: colors.textSecondary, textAlign: 'center', lineHeight: 1.6 }}>
            Hit send request first, and we'll detect available fields automatically.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ padding: '12px 16px 0' }}>
            <Box sx={{ display: 'flex', gap: '4px', backgroundColor: colors.tabBg, borderRadius: '6px', padding: '3px' }}>
              <Tooltip title="Structured: passes data as JSON with field names and nesting preserved." arrow>
                <Box
                  onClick={() => setMode('structured')}
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    backgroundColor: mode === 'structured' ? colors.background : 'transparent',
                    color: mode === 'structured' ? colors.tabActive : colors.tabInactive,
                    boxShadow: mode === 'structured' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.15s ease',
                  }}
                >
                  Structured
                </Box>
              </Tooltip>
              <Tooltip title="Raw mode: passes only the values, one per line — no field names or JSON structure." arrow>
                <Box
                  onClick={() => setMode('raw')}
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    backgroundColor: mode === 'raw' ? colors.background : 'transparent',
                    color: mode === 'raw' ? colors.tabActive : colors.tabInactive,
                    boxShadow: mode === 'raw' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.15s ease',
                  }}
                >
                  Raw Values
                </Box>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
            <FilterFieldList
              fields={fields}
              selectedPaths={selectedPaths}
              onTogglePath={handleTogglePath}
              onToggleAll={handleToggleAll}
              theme={theme}
            />
          </Box>
          <Box sx={{ padding: '0 16px 12px' }}>
            <FilterPreview
              jsonOutput={jsonOutput}
              selectedPaths={selectedPaths}
              mode={mode}
              theme={theme}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              padding: '12px 16px',
              borderTop: `1px solid ${colors.border}`,
            }}
          >
            <Button
              onClick={handleClear}
              variant="text"
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 500,
                color: colors.buttonSecondaryText,
                backgroundColor: colors.buttonSecondary,
                borderRadius: '6px',
                padding: '6px 16px',
                '&:hover': { backgroundColor: colors.border },
              }}
            >
              Clear
            </Button>
            <Button
              onClick={handleDone}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 500,
                backgroundColor: colors.accent,
                borderRadius: '6px',
                padding: '6px 16px',
                boxShadow: 'none',
                '&:hover': { backgroundColor: colors.accentHover, boxShadow: 'none' },
              }}
            >
              Done
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

function findFieldByPath(fields: import('./types').FilterField[], path: string): import('./types').FilterField | null {
  for (const field of fields) {
    if (field.path === path) return field;
    if (field.children) {
      const found = findFieldByPath(field.children, path);
      if (found) return found;
    }
  }
  return null;
}
